<template>
    <b-modal :id="id" v-model="visible" size="xl" title="Split Document" @show="visibleToggled" @ok="okClicked"
        scrollable hide-header-close>
        <b-row v-if="imageSrc?.length" class="source">
            <b-col>
                <h6>Source Pages</h6>
                <draggable v-model="imageSrc" :draggable="'.item'" group="pages"
                    :class="'row row-cols-9' + draggableClass" :force-fallback="true">
                    <template #header>
                        <b-col cols="12" class="text-center">
                            <b-spinner v-show="loading" class="align-middle" />
                        </b-col>
                    </template>
                    <b-col v-for="(img, index) in imageSrc" :key="'image_' + (index - 1)" cols="1" class="item">
                        <b-img :src="img.url" thumbnail block />
                    </b-col>
                </draggable>
            </b-col>
        </b-row>
        <b-row id="destination-row">
            <b-col cols="12" v-for="(group, index) in groups" :key="'group_' + (index + 1)">
                <b-row>
                    <b-col>
                        <h6 class="mt-3">
                            Group {{ index + 1 }}
                            <template v-if="group.destination[0]">
                                - {{ group.destination[0] }}
                            </template>
                        </h6>
                    </b-col>
                </b-row>
                <b-row align-v="end">
                    <b-col cols="auto">
                        Destination:
                    </b-col>
                    <b-col cols="auto">
                        <b-form-checkbox-group v-model="groups[index].destination" buttons size="sm">
                            <b-form-checkbox value="Patient Document" @change="filterGroups"
                                :button-variant="variant(index, 'Patient Document')">
                                Patient Document
                            </b-form-checkbox>
                            <b-form-checkbox value="New Script" @change="filterGroups"
                                :button-variant="variant(index, 'New Script')">
                                New Script
                            </b-form-checkbox>
                            <b-form-checkbox value="Discard" @change="filterGroups"
                                :button-variant="variant(index, 'Discard')">
                                Discard
                            </b-form-checkbox>
                        </b-form-checkbox-group>
                    </b-col>
                </b-row>
                <b-row v-if="groups[index].destination.includes('Patient Document')" class="mt-1" align-v="baseline">
                    <b-col>
                        <b-input v-model="groups[index].fileName" placeholder="File Name"
                            :state="fileNameInputState(groups[index].fileName)" required />
                    </b-col>
                    <b-col>
                        <search-component :id="groups[index].patient && +groups[index].patient.id"
                            v-model="groups[index].patient" :hide-label="true" placeholder="Patient"
                            search-u-r-l="/Patient" create-new-object clearable createNewLabel="Create New Patient"
                            @createNewObject="groups[index].showPatientForm = true" required
                            :state="patientSearchInputState(groups[index].patient)" />
                    </b-col>
                    <b-col cols="12" v-if="availableTags?.length">
                        <label>Tags<span v-if="tagsRequired"> *</span></label>
                        <multiselect v-model="groups[index].tags" :options="availableTags" multiple />
                    </b-col>

                </b-row>
                <b-row :class="(groups[index].showPatientForm ? 'sticky' : '')">
                    <b-col>
                        <!--suppress HtmlWrongAttributeValue draggable doesn't like .item-->
                        <draggable v-model="groups[index].images" draggable=".item" group="pages"
                            class="w-100 h-100 text-center mt-1 row" style="border: 1px dashed; min-height: 100px;">
                            <template #header>
                                <b-col cols="12">
                                    Drop pages here.
                                </b-col>
                            </template>
                            <b-col v-for="(img, idx) in groups[index].images"
                                :key="'group_' + index + '_image_' + (idx - 1)" cols="1" class="item" style="min-width: 100px"
                                @click="selectedImage = img.url" >
                                <b-img :src="img.url" thumbnail block />
                            </b-col>
                        </draggable>
                    </b-col>
                </b-row>
                <b-row v-if="groups[index].showPatientForm">
                    <b-col cols="5">
                        <b-img :src="srcForGroup(groups[index])" class="sticky-2"/>
                    </b-col>
                    <b-col v-if="groups[index].showPatientForm">
                        <PatientFormComponent :button-titles="patientButtonTitles" create-new-patient hide-insurance
                            @close="closePatientForm" @created="onPatientCreated" />
                    </b-col>
                </b-row>
            </b-col>

            <b-col v-if="groups.length < (totalPages ?? 0)" cols="12">
                <b-button variant="success" @click="addGroup">
                    Add Group
                </b-button>
            </b-col>
        </b-row>
    </b-modal>
</template>

<script lang="ts">
import { Vue, Component, Prop, VModel } from 'vue-property-decorator';
import axios from "axios";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import draggable from 'vuedraggable';
import { BvEvent } from "bootstrap-vue";
import SearchComponent from "@/components/SearchComponent.vue";
import { Patient } from "@/models/Patient";
import { QueueItem } from "@/models/QueueItem";
import PatientFormComponent from "@/components/Patient/PatientFormComponent.vue";
import Multiselect from "vue-multiselect";

type PageImage = {
    pageNumber: number,
    url: string,
}
type Group = {
    destination: string[],
    images: PageImage[],
    fileName: string | null,
    tags: string[],
    patient: Patient,
    showPatientForm: boolean,
}
@Component({
    name: "PdfChopperModal",
    components: {
        Multiselect,
        PatientFormComponent,
        SearchComponent,
        draggable,
    },
})
export default class PdfChopperModal extends Vue {
    @VModel() visible!: boolean;
    @Prop({ type: String, default: "pdf-chopper" }) id!: string;
    @Prop() imageId!: number;
    protected imageSrc?: PageImage[] = [];
    protected pages: number[] = [];
    protected groups: Group[] = [
        { destination: [], images: [], fileName: null, tags: [], patient: new Patient(), showPatientForm: false },
        { destination: [], images: [], fileName: null, tags: [], patient: new Patient(), showPatientForm: false },
    ];
    protected availableTags: string[] = [];
    private requirePatientDocumentTags = false;

    protected patientButtonTitles = {
        new: "",
        clear: "",
        save: "Save Patient",
        cancel: "Close Patient Form",
    };

    protected selectedImage: string = "";

    closePatientForm() {
        this.groups.forEach(g => g.showPatientForm = false);
    }

    onPatientCreated(newPatient: Patient) {
        this.groups.forEach(g => {
            if (g.showPatientForm) {
                g.patient = newPatient;
                g.showPatientForm = false;
            }
        });
    }

    protected loading: boolean = false;
    private shouldValidate = false;

    created() {
        console.log("%c Created PdfChopperModal", "color: green;");
        this.getTags();
        this.getConfiguration();
    }

    filterGroups() {
        this.groups = this.groups.map(g => ({
            destination: [g.destination[g.destination.length - 1]],
            images: g.images,
            fileName: g.fileName,
            tags: g.tags,
            patient: g.patient,
            showPatientForm: g.showPatientForm,
        }));
    }

    addGroup() {
        this.groups.push({
            destination: [],
            images: [],
            fileName: null,
            tags: [],
            patient: new Patient(),
            showPatientForm: false
        });
    }

    visibleToggled() {
        this.groups = [];
        this.addGroup();
        this.addGroup();

        this.fetchImageSource();
    }

    variant(index: number, value: string): string {
        if (this.groups[index] == undefined || this.groups[index].destination == undefined) {
            console.log("secondary: undefined!!", index, value, this.groups[index]);
            return "secondary";
        }
        if (this.groups[index].destination.includes(value)) {
            return "success";
        }

        if (this.shouldValidate && this.groups[index].images.length) {
            if ((this.groups[index].destination?.length ?? 0) === 0) {
                return "danger";
            }
            if (this.groups[index].destination[0] === undefined) {
                return "danger";
            }
        }
        return "secondary";
    }

    srcForGroup(group: any) {
        if (this.selectedImage?.length > 0) return this.selectedImage;
        return group.images[0]?.url;
    }

    get draggableClass(): string {
        if (this.shouldValidate && !this.imageSrc?.length) return " is-invalid";
        return "";
    }

    patientSearchInputState(patient: Patient): boolean | null {
        if (!this.shouldValidate) return null;
        return !!patient?.id;
    }

    fileNameInputState(fileName: string | null): boolean | null {
        if (!this.shouldValidate) return null;
        return !!fileName?.length;
    }

    validate(): boolean {
        this.shouldValidate = true;
        if (this.imageSrc?.length ?? 0 > 0) {
            this.$notification(NotificationOptions.error("You must move all pages to a group."));
            return false;
        }

        const hasIncompletePatientDocuments = this.groups.some(g => g.destination.includes("Patient Document") && g.images.length > 0 &&
            (g.fileName == null || g.fileName.trim() === "" || g.patient == null || g.patient.id == null || g.patient.id === 0));
        if (hasIncompletePatientDocuments) {
            this.$notification(NotificationOptions.error("Patient Document sections must have a patient and file name specified."));
            return false;
        }

        const hasIncompleteDestinations = this.groups.some(g => g.images.length > 0 && (g.destination.length === 0 || g.destination[0] === undefined));
        if (hasIncompleteDestinations) {
            this.$notification(NotificationOptions.error("You must specify a destination for each group with pages in it."));
            return false;
        }

        // Check if tags are required and missing for Patient Document groups
        if (this.tagsRequired) {
            const hasMissingTags = this.groups.some(g => g.destination.includes("Patient Document") && g.images.length > 0 &&
                (!g.tags || g.tags.length === 0));
            if (hasMissingTags) {
                this.$notification(NotificationOptions.error("Patient Document sections must have at least one tag selected."));
                return false;
            }
        }

        return true;
    }

    async okClicked(event: BvEvent) {
        event.preventDefault();

        if (!this.validate()) return;

        try {
            const response = await axios.post<QueueItem[]>(`/api/image/${this.imageId}/split`, this.groups);
            this.$emit("newQueueItems", response.data);
            if ((event.vueTarget as any).hide) {
                (event.vueTarget as any).hide();
            }
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
    }

    async getTags() {
        try {
            const response = await axios.get("/api/Configuration/PatientDocumentTags");
            this.availableTags = response.data;
        } catch (err) {
            console.error(err);
        }
    }

    async getConfiguration() {
        try {
            const response = await axios.get("/api/Configuration/RequirePatientDocumentTags");
            this.requirePatientDocumentTags = response.data;
        } catch (err) {
            console.error(err);
        }
    }

    get tagsRequired() {
        return this.requirePatientDocumentTags && this.availableTags.length > 0;
    }

    protected totalPages = 0;

    async fetchImageSource() {
        try {
            this.shouldValidate = false;
            this.loading = true;
            const imageUrl = `/api/image/${this.imageId}/pages`;
            this.imageSrc = [];

            const imageResp = await axios.get<[]>(imageUrl);
            for (let i = 0; i < imageResp.data.length; i++) {
                const blob = imageResp.data[i];
                this.imageSrc.push({ pageNumber: i, url: `data:image/webp;base64,${blob}` });
            }
            this.totalPages = this.imageSrc?.length ?? 0;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
        }
        this.loading = false;
    }

}
</script>

<style scoped>
::v-deep(.img-thumbnail) {
    transform: scale(1);
    transition: ease-in-out 300ms;

    &:hover {
        transition-delay: 1600ms;
        transform: scale(7.5);
        z-index: 1000;
        position: relative;
        transform-origin: 0 0;
    }
}

/*noinspection CssUnusedSymbol*/
.is-invalid {
    border: 1px solid #dc3545;
    background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' fill='none' stroke='%23dc3545' viewBox='0 0 12 12'%3e%3ccircle cx='6' cy='6' r='4.5'/%3e%3cpath stroke-linejoin='round' d='M5.8 3.6h.4L6 6.5z'/%3e%3ccircle cx='6' cy='8.2' r='.6' fill='%23dc3545' stroke='none'/%3e%3c/svg%3e");
    background-repeat: no-repeat;
    background-position: right calc(0.375em + 0.1875rem) center;
    background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
}

.source {
    position: sticky;
    top: -24px;
    z-index: 300;
    padding: 10px 0;
    background: white;
}
.sticky {
    position: sticky;
    top: -24px;
    z-index: 300;
    padding: 10px 0;
    background: white;
}
.sticky-2 {
    position: sticky;
    top: 120px;
    z-index: 200;
    padding: 10px 0;
    background: white;
    width: 100%;
}
</style>
